import React, { useState, useEffect } from 'react';
import { TestMetadata } from '../types/testy';
import { testLoaderService } from '../services/testLoaderService';
import TestCard from './TestCard';
import LoadingComponents from './LoadingComponents';

interface TestListPageProps {
  onNavigate: (section: string, testId?: string) => void;
}

const TestListPage: React.FC<TestListPageProps> = ({ onNavigate }) => {
  const [tests, setTests] = useState<TestMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'free' | 'premium'>('all');

  useEffect(() => {
    loadAvailableTests();
  }, []);

  const loadAvailableTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const availableTests = await testLoaderService.getAvailableTests();
      setTests(availableTests);
    } catch (err) {
      console.error('Failed to load available tests:', err);
      setError('Nepodařilo se načíst dostupné testy. Zkuste to prosím později.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = (testId: string) => {
    // Navigate to test start page
    onNavigate('test-start', testId);
  };

  const handleRetry = () => {
    loadAvailableTests();
  };

  const filteredTests = tests.filter(test => {
    switch (filter) {
      case 'free':
        return test.isFreeSample;
      case 'premium':
        return !test.isFreeSample;
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
            Dostupné testy
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins">
            Vyberte si test pro procvičování základů společenských věd
          </p>
        </div>
        
        <div className="space-y-4">
          <LoadingComponents.Card />
          <LoadingComponents.Card />
          <LoadingComponents.Card />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
            Dostupné testy
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins">
            Vyberte si test pro procvičování základů společenských věd
          </p>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2 font-poppins">
            Chyba při načítání testů
          </h3>
          <p className="text-red-700 dark:text-red-300 font-poppins mb-4">
            {error}
          </p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
          >
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          Dostupné testy
        </h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins">
          Vyberte si test pro procvičování základů společenských věd
        </p>
      </header>

      {/* Filter buttons */}
      <nav className="mb-6" role="navigation" aria-label="Filtrování testů">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
              filter === 'all'
                ? 'bg-blue-600 text-white dark:bg-blue-500'
                : 'bg-white dark:bg-dark-card text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-hover dark:hover:bg-dark-hover border border-border'
            }`}
            aria-pressed={filter === 'all'}
            aria-label={`Zobrazit všechny testy, celkem ${tests.length} testů`}
          >
            Všechny testy ({tests.length})
          </button>
          <button
            onClick={() => setFilter('free')}
            className={`px-4 py-2 rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
              filter === 'free'
                ? 'bg-blue-600 text-white dark:bg-blue-500'
                : 'bg-white dark:bg-dark-card text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-hover dark:hover:bg-dark-hover border border-border'
            }`}
            aria-pressed={filter === 'free'}
            aria-label={`Zobrazit pouze bezplatné testy, celkem ${tests.filter(t => t.isFreeSample).length} testů`}
          >
            Zdarma ({tests.filter(t => t.isFreeSample).length})
          </button>
          <button
            onClick={() => setFilter('premium')}
            className={`px-4 py-2 rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
              filter === 'premium'
                ? 'bg-blue-600 text-white dark:bg-blue-500'
                : 'bg-white dark:bg-dark-card text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-hover dark:hover:bg-dark-hover border border-border'
            }`}
            aria-pressed={filter === 'premium'}
            aria-label={`Zobrazit pouze prémiové testy, celkem ${tests.filter(t => !t.isFreeSample).length} testů`}
          >
            Pro členy ({tests.filter(t => !t.isFreeSample).length})
          </button>
        </div>
      </nav>

      {/* Test list */}
      <main role="main" aria-label="Seznam testů">
        {filteredTests.length === 0 ? (
          <div className="text-center py-12" role="status">
            <div className="text-4xl sm:text-6xl mb-4" aria-hidden="true">📝</div>
            <h2 className="text-lg sm:text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
              {filter === 'free' ? 'Žádné bezplatné testy' : 
               filter === 'premium' ? 'Žádné prémiové testy' : 
               'Žádné testy k dispozici'}
            </h2>
            <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
              {filter === 'free' ? 'Momentálně nejsou k dispozici žádné bezplatné testy.' :
               filter === 'premium' ? 'Momentálně nejsou k dispozici žádné prémiové testy.' :
               'Momentálně nejsou k dispozici žádné testy.'}
            </p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                Zobrazit všechny testy
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4" role="list" aria-label={`${filteredTests.length} dostupných testů`}>
            {filteredTests.map((test) => (
              <div key={test.id} role="listitem">
                <TestCard
                  test={test}
                  onStartTest={handleStartTest}
                  onNavigate={onNavigate}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Info section */}
      <div className="mt-12 bg-white dark:bg-dark-card rounded-lg p-6 border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-sm">
        <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-3 font-poppins">
          O testech ZSV
        </h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm text-light-text-secondary dark:text-dark-text-secondary font-poppins">
          <div>
            <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              Formát testů
            </h4>
            <ul className="space-y-1">
              <li>• 60 otázek rozdělených do 3 sekcí</li>
              <li>• Časový limit 60 minut</li>
              <li>• Výběr ze 4 možností (A, B, C, D)</li>
              <li>• Možnost označování otázek</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              Hodnocení
            </h4>
            <ul className="space-y-1">
              <li>• +1 bod za správnou odpověď</li>
              <li>• -1/3 bodu za špatnou odpověď</li>
              <li>• 0 bodů za nevyplněnou odpověď</li>
              <li>• Percentilové hodnocení výkonu</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestListPage;